import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateDefaultTipSettingsApplicationErrors,
  CreateDefaultTipSettingsResponse,
  CreateTipSettingsApplicationErrors,
  DeleteTipSettingsApplicationErrors,
  QueryTipSettingsResponse,
  TipSettings,
  TipSettingsQuery,
  TipSettingsQueryBuilder,
  UpdateTipSettingsApplicationErrors,
  createDefaultTipSettings as universalCreateDefaultTipSettings,
  createTipSettings as universalCreateTipSettings,
  deleteTipSettings as universalDeleteTipSettings,
  queryTipSettings as universalQueryTipSettings,
  typedQueryTipSettings as universalTypedQueryTipSettings,
  updateTipSettings as universalUpdateTipSettings,
} from './tips-settings-v1-tip-settings-tip-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function queryTipSettings(
  httpClient: HttpClient
): QueryTipSettingsSignature {
  return () =>
    universalQueryTipSettings(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryTipSettingsSignature {
  /**
   * Creates a query to retrieve a list of tip settings.
   *
   * The `queryTipSettings()` function builds a query to retrieve a list of tip settings and returns a `TipSettingsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/ecom/tip-settings/tip-settings-query-builder/find) function.
   *
   * You can refine the query by chaining `TipSettingsQueryBuilder` functions onto the query. `TipSettingsQueryBuilder` functions enable you to sort, filter, and control the results that `queryTipSettings()` returns.
   *
   * `queryTipSettings()` runs with the following `TipSettingsQueryBuilder` default that you can override:
   *
   * + `limit` is `50`.
   * + Sorted by `id` in ascending order.
   *
   * The functions that are chained to `queryTipSettings()` are applied in the order they are called. For example, if you apply `ascending("tipType")` and then `ascending("locationId")`, the results are sorted first by the `"tipType"`, and then, if there are multiple results with the same `"tipType"`, the items are sorted by `"locationId"`.
   *
   * The following `TipSettingsQueryBuilder` functions are supported for the `queryTipSettings()` function. For a full description of the tip settings object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/ecom/tip-settings/tip-settings-query-result/items) property in `TipSettingsQueryResult`.
   */
  (): TipSettingsQueryBuilder;
}

export function typedQueryTipSettings(
  httpClient: HttpClient
): TypedQueryTipSettingsSignature {
  return (query: TipSettingsQuery) =>
    universalTypedQueryTipSettings(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryTipSettingsSignature {
  /** */
  (query: TipSettingsQuery): Promise<
    NonNullablePaths<
      QueryTipSettingsResponse,
      | `tipSettings`
      | `tipSettings.${number}.tipType`
      | `tipSettings.${number}.staffDistributionMethod`,
      4
    >
  >;
}

export function updateTipSettings(
  httpClient: HttpClient
): UpdateTipSettingsSignature {
  return (tipSettings: TipSettings) =>
    universalUpdateTipSettings(
      tipSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTipSettingsSignature {
  /**
   * Updates a `tipSettings` object.
   *
   *
   * You can't update the `default` field.
   * @param - Tip settings to update.
   * @param - Options to use when updating tip settings.
   * @returns Updated tip settings.
   */
  (tipSettings: TipSettings): Promise<
    NonNullablePaths<
      TipSettings,
      `tipType` | `presets` | `staffDistributionMethod`,
      2
    > & {
      __applicationErrorsType?: UpdateTipSettingsApplicationErrors;
    }
  >;
}

export function createTipSettings(
  httpClient: HttpClient
): CreateTipSettingsSignature {
  return (tipSettings: TipSettings) =>
    universalCreateTipSettings(
      tipSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateTipSettingsSignature {
  /**
   * Creates a `tipSettings` object.
   *
   *
   * You can't create a `tipSettings` object with `{"default": true}`.
   * @param - Tip settings to create.
   * @returns Created tip settings.
   */
  (tipSettings: TipSettings): Promise<
    NonNullablePaths<
      TipSettings,
      `tipType` | `presets` | `staffDistributionMethod`,
      2
    > & {
      __applicationErrorsType?: CreateTipSettingsApplicationErrors;
    }
  >;
}

export function createDefaultTipSettings(
  httpClient: HttpClient
): CreateDefaultTipSettingsSignature {
  return (tipSettings: TipSettings) =>
    universalCreateDefaultTipSettings(
      tipSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDefaultTipSettingsSignature {
  /**
   * Creates a default `tipSettings` object.
   *
   *
   * The call fails if there is already an existing default `tipSettings` object.
   * @param - Default tip settings to create.
   */
  (tipSettings: TipSettings): Promise<
    NonNullablePaths<
      CreateDefaultTipSettingsResponse,
      | `tipSettings.tipType`
      | `tipSettings.presets`
      | `tipSettings.staffDistributionMethod`,
      3
    > & {
      __applicationErrorsType?: CreateDefaultTipSettingsApplicationErrors;
    }
  >;
}

export function deleteTipSettings(
  httpClient: HttpClient
): DeleteTipSettingsSignature {
  return (tipSettingsId: string) =>
    universalDeleteTipSettings(
      tipSettingsId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTipSettingsSignature {
  /**
   * Deletes a `tipSettings` object.
   *
   *
   * You can't delete the default `tipSettings` object.
   * @param - ID of the `tipSettings` object to delete.
   */
  (tipSettingsId: string): Promise<
    void & {
      __applicationErrorsType?: DeleteTipSettingsApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  CreateDefaultTipSettingsRequest,
  CreateDefaultTipSettingsResponse,
  CreateTipSettingsRequest,
  CreateTipSettingsResponse,
  CursorPaging,
  Cursors,
  DeleteTipSettingsRequest,
  DeleteTipSettingsResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  Paging,
  PagingMetadataV2,
  Preset,
  QueryTipSettingsRequest,
  QueryTipSettingsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  SortOrder,
  Sorting,
  StaffDistributionMethod,
  TipSettings,
  TipSettingsIdentifierOneOf,
  TipSettingsQueryBuilder,
  TipSettingsQueryResult,
  TipSettingsQuerySpec,
  TipType,
  UpdateTipSettingsRequest,
  UpdateTipSettingsResponse,
  WebhookIdentityType,
  utils,
} from './tips-settings-v1-tip-settings-tip-settings.universal.js';
